var _extends = Object.assign || function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; };

var _templateObject = _taggedTemplateLiteralLoose(["\n    padding-top: ", ";\n    padding-bottom: ", ";\n  "], ["\n    padding-top: ", ";\n    padding-bottom: ", ";\n  "]),
    _templateObject2 = _taggedTemplateLiteralLoose(["\n    width: auto;\n    ", ";\n  "], ["\n    width: auto;\n    ", ";\n  "]);

function _taggedTemplateLiteralLoose(strings, raw) { strings.raw = raw; return strings; }

import React from "react";
import PropTypes from "prop-types";
import pick from "lodash/pick";
import styled, { css } from "styled-components";
// Size-saver for polished
import lighten from "polished/lib/color/lighten.js";
import darken from "polished/lib/color/darken.js";
import transparentize from "polished/lib/color/transparentize.js";
import getLuminance from "polished/lib/color/getLuminance.js";
import { media } from "../mixins";

var Button = function Button(props) {
  var passProps = pick(props, ["as", "type", "className", "onClick", "color", "basic", "fluid", "pill", "theme", "style", "disabled", "selectedScreen", "lightText"]);
  var style = props.style || {};

  // inline styling to avoid excessive number of generated classes
  if (!isNaN(props.progress)) {
    style.background = "linear-gradient(to right,\n      " + bgColor(props) + ",\n      " + bgColor(props) + " " + props.progress + "%,\n      " + "#f4f4f4" + " " + props.progress + "%\n    )";
  }

  var screens = props.screens ? props.screens.map(function (s) {
    return typeof s === "string" ? { content: s } : s;
  }) : [{ content: props.children, icon: props.icon }];

  return React.createElement(
    StyledButton,
    _extends({ ref: props.ref }, passProps),
    props.inject,
    screens.map(function (s, i) {
      return React.createElement(
        ButtonScreen,
        {
          key: i,
          i: i,
          thin: props.thin,
          ultrathin: props.ultrathin,
          wide: props.wide,
          selectedScreen: props.selectedScreen,
          progress: props.progress,
          uppercase: props.uppercase,
          lightText: props.lightText
        },
        s.icon ? React.createElement(
          "div",
          { className: "flex" },
          React.createElement(
            "div",
            { className: "content" },
            s.content
          ),
          React.createElement(
            "div",
            { className: "icon" },
            React.createElement(s.icon, null)
          )
        ) : s.content
      );
    })
  );
};

function getYAxisPadding(_ref) {
  var thin = _ref.thin,
      ultrathin = _ref.ultrathin;

  if (ultrathin) {
    return "0.5em";
  } else if (thin) {
    return "1em";
  } else {
    return "1.5em";
  }
}

export var ButtonScreen = styled.div.withConfig({
  displayName: "Button__ButtonScreen",
  componentId: "bc776a-0"
})(["display:block;padding:", ";", ";transition:top 0.2s ease-in-out;text-transform:", ";letter-spacing:", ";", " text-shadow:", ";", ";.flex{display:flex;.content{flex:1;}.icon{align-self:right;font-size:1.5em;margin:-5.5px 0 -5.5px -1.5em;}}"], function (props) {
  return getYAxisPadding(props);
}, function (props) {
  return props.wide ? "3em" : "1em";
}, function (props) {
  return props.uppercase ? "uppercase" : "none";
}, function (props) {
  return props.uppercase ? "1px" : "0px";
}, media.desktop(_templateObject, function (props) {
  return getYAxisPadding(props);
}, function (props) {
  return getYAxisPadding(props);
}), function (props) {
  return props.progress ? "0px 0px 7px rgba(0,0,0,0.25);" : "none;";
}, function (props) {
  return props.i === 0 ? css(["position:relative;top:", "em;"], props.selectedScreen * -3) : css(["position:absolute;top:", "em;left:0;right:0;"], (props.i - props.selectedScreen) * 3);
});

var bgColor = function bgColor(props) {
  return props.theme[props.color];
};
var fgColor = function fgColor(props) {
  var theme = props.theme;

  var lum = getLuminance(bgColor(props));
  return lum > 0.6 ? theme.secondary : theme.background;
};
var hoverColor = function hoverColor(props) {
  var bg = bgColor(props);
  if (getLuminance(bg) < 0.9) {
    return lighten(0.05, bg);
  } else {
    return darken(0.05, bg);
  }
};

var StyledButton = styled.button.withConfig({
  displayName: "Button__StyledButton",
  componentId: "bc776a-1"
})(["font-family:\"Gilroy\",sans-serif;display:", ";width:100%;position:relative;padding:0;background:", ";color:", ";font-size:1em;line-height:1;font-weight:", ";text-align:center;cursor:pointer;border:0;border-radius:", ";outline:none;overflow:hidden;transition:background-color 0.1s ease-out;", " &:hover:enabled{background:", ";}&:active:enabled{background:", ";}&:disabled{cursor:default;opacity:0.7;}", ";"], function (props) {
  return props.fluid ? "block" : "inline-block";
}, bgColor, fgColor, function (props) {
  return props.lightText ? "normal" : "bold";
}, function (props) {
  return props.pill ? "1000px" : "8px";
}, media.desktop(_templateObject2, function (props) {
  return props.fluid && !props.as && css(["width:100%;"]);
}), function (props) {
  return hoverColor(props);
}, function (props) {
  return darken(0.1, hoverColor(props));
}, function (props) {
  return props.basic && css(["border:2px solid currentColor;background:transparent;color:", ";&:hover:enabled{background:", ";color:", ";border-color:", ";}&:active:enabled{background:", ";}"], bgColor, function (props) {
    return transparentize(0.9, bgColor(props));
  }, function (props) {
    return lighten(props.primary ? 0.1 : 0.05, bgColor(props));
  }, function (props) {
    return lighten(props.primary ? 0.15 : 0.05, bgColor(props));
  }, function (props) {
    return transparentize(0.8, bgColor(props));
  });
});

Button.propTypes = process.env.NODE_ENV !== "production" ? {
  as: PropTypes.oneOfType([PropTypes.string, PropTypes.element]),
  screens: PropTypes.arrayOf(PropTypes.oneOfType([PropTypes.string, PropTypes.shape({ content: PropTypes.node, icon: PropTypes.any })])),
  selectedScreen: PropTypes.number,
  color: PropTypes.string,
  basic: PropTypes.bool,
  pill: PropTypes.bool,
  wide: PropTypes.bool,
  thin: PropTypes.bool,
  ultrathin: PropTypes.bool,
  inject: PropTypes.node,
  uppercase: PropTypes.bool,
  lightText: PropTypes.bool
} : {};

Button.defaultProps = {
  selectedScreen: 0,
  color: "primary",
  pill: false
};

export default styled(Button).withConfig({
  displayName: "Button",
  componentId: "bc776a-2"
})([""]);